import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { 
  Users, Shield, Edit2, Save, X, Check, AlertCircle, 
  ChevronDown, Plus, History
} from 'lucide-react';
import { toast } from 'sonner';

import { API } from '../config/api';

export default function TeamSelection({ childId, childName, onTeamAssigned }) {
  const [assignment, setAssignment] = useState(null);
  const [availableTeams, setAvailableTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);
  const [currentSeason, setCurrentSeason] = useState('');
  const [needsSelection, setNeedsSelection] = useState(false);
  
  const [formData, setFormData] = useState({
    team_name: '',
    custom_team: '',
    sport: '',
    position: '',
    jersey_number: ''
  });

  const fetchAvailableTeams = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API}/player-team/available-teams/list`, { headers });
      setAvailableTeams(res.data.teams || []);
    } catch (err) {
      console.error('Failed to fetch available teams');
    }
  }, []);

  const fetchTeamAssignment = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API}/player-team/${childId}`, { headers });
      
      // Use current_assignment if available, otherwise create a pseudo-assignment from profile data
      let effectiveAssignment = res.data.current_assignment;
      if (!effectiveAssignment && res.data.profile_team) {
        effectiveAssignment = {
          team_name: res.data.profile_team,
          season: res.data.current_season,
          from_profile: true  // Flag to indicate this is from profile, not player_team_assignments
        };
      }
      
      setAssignment(effectiveAssignment);
      setCurrentSeason(res.data.current_season);
      setNeedsSelection(res.data.needs_team_selection);
      setHistory(res.data.history || []);
      
      // Auto-show form if team selection is required
      if (res.data.needs_team_selection) {
        setShowForm(true);
      }
      
      // Pre-fill form if editing
      if (effectiveAssignment) {
        setFormData({
          team_name: effectiveAssignment.team_name || '',
          custom_team: '',
          sport: effectiveAssignment.sport || '',
          position: effectiveAssignment.position || '',
          jersey_number: effectiveAssignment.jersey_number || ''
        });
      }
    } catch (err) {
      console.error('Failed to fetch team assignment');
    } finally {
      setLoading(false);
    }
  }, [childId]);

  useEffect(() => {
    if (childId) {
      fetchTeamAssignment();
      fetchAvailableTeams();
    }
  }, [childId, fetchTeamAssignment, fetchAvailableTeams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const teamName = formData.team_name === 'custom' ? formData.custom_team : formData.team_name;
    
    if (!teamName.trim()) {
      toast.error('Please select or enter a team name');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(`${API}/player-team/assign`, {
        child_id: childId,
        team_name: teamName,
        sport: formData.sport || null,
        position: formData.position || null,
        jersey_number: formData.jersey_number || null
      }, { headers });
      
      toast.success('Team assigned successfully!');
      setShowForm(false);
      setNeedsSelection(false);
      fetchTeamAssignment();
      
      if (onTeamAssigned) {
        onTeamAssigned(teamName);
      }
    } catch (err) {
      toast.error('Failed to assign team');
    }
  };

  const handleEdit = () => {
    // Pre-fill form with current assignment data before showing form
    if (assignment) {
      setFormData({
        team_name: assignment.team_name || '',
        custom_team: '',
        sport: assignment.sport || '',
        position: assignment.position || '',
        jersey_number: assignment.jersey_number || ''
      });
    }
    setShowForm(true);
  };

  if (loading) {
    return (
      <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 animate-pulse">
        <div className="h-6 bg-zinc-800 rounded w-32 mb-2" />
        <div className="h-10 bg-zinc-800 rounded" />
      </div>
    );
  }

  // Modal overlay when team selection is required
  if (needsSelection && !assignment) {
    return (
      <div 
        className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" 
        data-testid="team-selection-modal"
        onClick={(e) => {
          // Close modal when clicking outside
          if (e.target === e.currentTarget) {
            setNeedsSelection(false);
          }
        }}
      >
        <div className="bg-zinc-900 border border-zinc-700 rounded-2xl max-w-md w-full p-6 shadow-2xl">
          {/* Close button */}
          <div className="flex justify-end mb-2">
            <button
              onClick={() => setNeedsSelection(false)}
              className="p-1.5 text-zinc-500 hover:text-white hover:bg-zinc-800 rounded"
              title="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-gradient-to-br from-[#02b5ff] to-[#ff025f] rounded-full flex items-center justify-center mx-auto mb-4">
              <Shield className="h-8 w-8 text-white" />
            </div>
            <h2 className="text-xl font-bold text-white mb-2">Team Selection Required</h2>
            <p className="text-zinc-400 text-sm">
              Please select your team for the {currentSeason} season
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-2">Team Name *</label>
              <select
                value={formData.team_name}
                onChange={(e) => setFormData({ ...formData, team_name: e.target.value })}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white focus:border-[#02b5ff] outline-none"
                required
              >
                <option value="">Select a team...</option>
                {availableTeams.map((team, i) => (
                  <option key={i} value={team.name}>{team.name}</option>
                ))}
                <option value="custom">+ Add new team</option>
              </select>
            </div>

            {formData.team_name === 'custom' && (
              <input
                type="text"
                placeholder="Enter team name"
                value={formData.custom_team}
                onChange={(e) => setFormData({ ...formData, custom_team: e.target.value })}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white focus:border-[#02b5ff] outline-none"
                required
              />
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-zinc-400 mb-1">Sport</label>
                <input
                  type="text"
                  placeholder="e.g., Football"
                  value={formData.sport}
                  onChange={(e) => setFormData({ ...formData, sport: e.target.value })}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:border-[#02b5ff] outline-none"
                />
              </div>
              <div>
                <label className="block text-xs text-zinc-400 mb-1">Position</label>
                <input
                  type="text"
                  placeholder="e.g., Striker"
                  value={formData.position}
                  onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:border-[#02b5ff] outline-none"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs text-zinc-400 mb-1">Jersey Number</label>
              <input
                type="text"
                placeholder="e.g., 10"
                value={formData.jersey_number}
                onChange={(e) => setFormData({ ...formData, jersey_number: e.target.value })}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:border-[#02b5ff] outline-none"
              />
            </div>

            <Button
              type="submit"
              className="w-full bg-gradient-to-r from-[#02b5ff] to-[#ff025f] text-white py-3 rounded-lg font-semibold"
            >
              <Check className="h-4 w-4 mr-2" /> Confirm Team
            </Button>
            
            <button
              type="button"
              onClick={() => setNeedsSelection(false)}
              className="w-full text-zinc-500 hover:text-zinc-300 text-sm py-2"
            >
              I'll do this later
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden" data-testid="team-selection">
      {/* Header */}
      <div className="p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-[#02b5ff] to-[#ff025f] rounded-full flex items-center justify-center">
            <Shield className="h-5 w-5 text-white" />
          </div>
          <div>
            <h3 className="font-bold text-white">Current Team</h3>
            <p className="text-xs text-zinc-500">Season {currentSeason}</p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {history.length > 1 && (
            <button
              onClick={() => setShowHistory(!showHistory)}
              className="p-2 text-zinc-500 hover:text-white"
              title="View history"
            >
              <History className="h-4 w-4" />
            </button>
          )}
          <Button
            size="sm"
            variant="outline"
            onClick={handleEdit}
            className="text-xs"
            data-testid="edit-team-btn"
          >
            <Edit2 className="h-3 w-3 mr-1" /> Edit
          </Button>
        </div>
      </div>

      {/* Current Assignment */}
      {assignment && !showForm && (
        <div className="px-4 pb-4">
          <div className="bg-zinc-800/50 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-[#02b5ff] to-[#ff025f] rounded-lg flex items-center justify-center">
                <Users className="h-6 w-6 text-white" />
              </div>
              <div className="flex-1">
                <h4 className="font-bold text-white text-lg">{assignment.team_name}</h4>
                <div className="flex flex-wrap gap-2 mt-1">
                  {assignment.sport && (
                    <span className="text-xs bg-zinc-700 text-zinc-300 px-2 py-0.5 rounded">{assignment.sport}</span>
                  )}
                  {assignment.position && (
                    <span className="text-xs bg-[#02b5ff]/20 text-[#02b5ff] px-2 py-0.5 rounded">{assignment.position}</span>
                  )}
                  {assignment.jersey_number && (
                    <span className="text-xs bg-[#ff025f]/20 text-[#ff025f] px-2 py-0.5 rounded">#{assignment.jersey_number}</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* No Assignment Yet */}
      {!assignment && !showForm && (
        <div className="px-4 pb-4">
          <div className="bg-zinc-800/30 border border-dashed border-zinc-700 rounded-lg p-6 text-center">
            <Users className="h-8 w-8 text-zinc-600 mx-auto mb-2" />
            <p className="text-zinc-400 text-sm mb-3">No team assigned yet</p>
            <Button
              size="sm"
              onClick={() => setShowForm(true)}
              className="bg-[#02b5ff] text-white"
            >
              <Plus className="h-3 w-3 mr-1" /> Assign Team
            </Button>
          </div>
        </div>
      )}

      {/* Edit Form */}
      {showForm && (
        <div className="px-4 pb-4">
          <form onSubmit={handleSubmit} className="bg-zinc-800/30 rounded-lg p-4 space-y-3">
            <div>
              <label className="block text-xs text-zinc-400 mb-1">Team Name *</label>
              <select
                value={formData.team_name}
                onChange={(e) => setFormData({ ...formData, team_name: e.target.value })}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:border-[#02b5ff] outline-none"
                data-testid="team-name-select"
              >
                <option value="">Select a team...</option>
                {/* Show current team first if it exists */}
                {formData.team_name && !availableTeams.some(t => t.name === formData.team_name) && formData.team_name !== 'custom' && (
                  <option value={formData.team_name}>{formData.team_name} (current)</option>
                )}
                {availableTeams.map((team, i) => (
                  <option key={i} value={team.name}>{team.name}</option>
                ))}
                <option value="custom">+ Add new team</option>
              </select>
            </div>

            {formData.team_name === 'custom' && (
              <input
                type="text"
                placeholder="Enter team name"
                value={formData.custom_team}
                onChange={(e) => setFormData({ ...formData, custom_team: e.target.value })}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:border-[#02b5ff] outline-none"
              />
            )}

            <div className="grid grid-cols-3 gap-2">
              <input
                type="text"
                placeholder="Sport"
                value={formData.sport}
                onChange={(e) => setFormData({ ...formData, sport: e.target.value })}
                className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:border-[#02b5ff] outline-none"
              />
              <input
                type="text"
                placeholder="Position"
                value={formData.position}
                onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:border-[#02b5ff] outline-none"
              />
              <input
                type="text"
                placeholder="Jersey #"
                value={formData.jersey_number}
                onChange={(e) => setFormData({ ...formData, jersey_number: e.target.value })}
                className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:border-[#02b5ff] outline-none"
              />
            </div>

            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setShowForm(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                size="sm"
                className="bg-[#02b5ff] text-white"
              >
                <Save className="h-3 w-3 mr-1" /> Save Changes
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* History */}
      {showHistory && history.length > 1 && (
        <div className="px-4 pb-4">
          <h5 className="text-xs text-zinc-500 mb-2 flex items-center gap-1">
            <History className="h-3 w-3" /> Team History
          </h5>
          <div className="space-y-1">
            {history.map((h, i) => (
              <div key={i} className="flex items-center justify-between bg-zinc-800/30 rounded p-2">
                <span className="text-sm text-zinc-300">{h.team_name}</span>
                <span className="text-xs text-zinc-500">{h.season}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Season Reset Notice */}
      {new Date().getMonth() === 7 && (
        <div className="px-4 pb-4">
          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 flex items-start gap-2">
            <AlertCircle className="h-4 w-4 text-yellow-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-yellow-400 font-medium">New Season Starting</p>
              <p className="text-xs text-zinc-400 mt-1">
                Teams reset every August 1st. Please confirm your team for the new season.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
